import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Chips,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { DISPLAY_TZ } from '../access/format.ts';
import {
  ANY_KIND,
  closedDialog,
  isCurrentGeneration,
  issuesPath,
  ISSUE_KIND,
  openDialog,
  closedBeforeLine,
  DEFAULT_PROVIDER,
  hidesOurSide,
  JILI_KIND_MEANING,
  jiliDetailLines,
  kindsFor,
  offersResend,
  PROVIDER_OPTIONS,
  providerLabel,
  OUR_SIDE,
  ourSideLabel,
  RESOLVE_REASON_MAX,
  resolvePlaceholder,
  verdictFor,
  resolveReasonError,
  saDetailLines,
  type IssueFilters,
  type ReconciliationIssue,
  type ReconciliationPage,
  type ResolveDialog,
} from './provider-reconciliation.ts';
import { reconciliationEmpty, reconciliationLead, type ReconciliationCheck } from './scope-words.ts';

/**
 * What the SA reconciliation, run every ten minutes, found that does not agree
 * (SA-112, SA-125): a bet SA has and we do not, one we have and SA does not, an
 * amount that differs, or a transaction SA reports as unsuccessful at its end.
 *
 * A reader restricted to some brands is sent only the findings placed in them
 * (SEC-002), and the answer says so; the notice and the empty state take their
 * words from it (scope-words.ts).
 *
 * READ with transactions.view. The two writes need funds.adjust — a role that
 * cannot sign in without a second factor — and are HIDDEN from anyone without
 * it rather than shown disabled:
 *   · Resend asks SA to replay the round's callbacks (ResendTransaction). SA
 *     allows one every five minutes; the server refuses inside that window and
 *     names the next allowed time, and that sentence is shown as it is.
 *   · Resolve closes the finding once somebody has dealt with it.
 * Both are confirmed first, and every refusal opens the error modal.
 *
 * Every finding says OUR SIDE: what our own ledger did with the bet, read when
 * the list is read. On a bet SA lists as unsuccessful that is the whole answer,
 * and only "stake taken, not settled" can leave anyone owed; on the other kinds
 * the two records disagree and the label settles nothing. Both dialogs say
 * which (`verdictFor`), and a finding closed before and reopened says so.
 *
 * The list is one status and one reason at a time, keyset-paged (Load more),
 * and takes a Manila-day range on when a finding was last seen. Every filter is
 * the server's, so a reason never shows an empty table with Load more under it.
 */

const WHEN = new Intl.DateTimeFormat('en-PH', {
  timeZone: DISPLAY_TZ,
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

type Action = { kind: 'resend' | 'resolve'; issue: ReconciliationIssue };

export function ProviderReconciliation() {
  const { allowed } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const canAct = allowed('funds.adjust');

  const [filters, setFilters] = useState<IssueFilters>({
    provider: DEFAULT_PROVIDER,
    status: 'OPEN',
    kind: ANY_KIND,
    from: '',
    to: '',
  });
  const [draftDates, setDraftDates] = useState({ from: '', to: '' });
  const [issues, setIssues] = useState<ReconciliationIssue[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  /* Whether the findings are only those of the reader's brands, from the
     answer they came in. Null until an answer is here. */
  const [brandRestricted, setBrandRestricted] = useState<boolean | null>(null);
  /* The check behind the list: whether the provider is connected here and when its check last
     finished a run. An empty open list is a clean result only if the check ran. */
  const [check, setCheck] = useState<ReconciliationCheck | null>(null);
  const [reload, setReload] = useState(0);
  const [dialog, setDialog] = useState<ResolveDialog<Action>>(closedDialog<Action>());
  const [busy, setBusy] = useState(false);
  /* Bumped whenever the filters change: an answer from an older generation is
     dropped rather than appended under filters it was not asked for. */
  const generation = useRef(0);

  const { action, reason, reasonTouched } = dialog;
  const status = filters.status;
  const provider = filters.provider;
  const showOurSide = !hidesOurSide(provider);
  const providerName = providerLabel(provider);
  /* The name the table's caption and game-ID header use: "SA", as they read before JILI, so SA's
     screen says what it said; another provider's full name otherwise. */
  const tableName = provider === 'SA' ? 'SA' : providerName;

  const load = useCallback(
    async (cursor: string | null) => {
      const mine = generation.current;
      try {
        const page = await api.get<ReconciliationPage>(issuesPath(filters, cursor));
        if (!isCurrentGeneration(mine, generation.current)) return;
        setIssues((current) => (cursor ? [...current, ...page.items] : page.items));
        setNextCursor(page.nextCursor);
        setBrandRestricted(page.brandRestricted);
        setCheck(page.check ?? null);
        setFailed(false);
      } catch (error) {
        if (!isCurrentGeneration(mine, generation.current)) return;
        if (!cursor) {
          setIssues([]);
          setFailed(true);
        }
        showError({
          title: 'Could not load reconciliation findings',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      }
    },
    [filters, showError],
  );

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;
    setNextCursor(null);
    setLoading(true);
    /* Only the read this effect asked may end the loading state. An older
       read's answer is dropped by `load`, but its finally runs all the same,
       and if it ended the loading while the newer read was still out, the
       empty state (nothing read yet) would say the findings could not be read
       when nothing had failed. */
    void load(null).finally(() => {
      if (isCurrentGeneration(mine, generation.current)) setLoading(false);
    });
  }, [load, reload]);

  const changeFilters = useCallback((next: Partial<IssueFilters>) => {
    setFilters((current) => ({ ...current, ...next }));
  }, []);

  const reasonError = resolveReasonError(reason);

  const confirm = useCallback(async () => {
    if (!action) return;
    if (action.kind === 'resolve' && resolveReasonError(reason) !== null) {
      setDialog((d) => ({ ...d, reasonTouched: true }));
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/api/games/provider-reconciliation/issues/${encodeURIComponent(action.issue.id)}/${action.kind}`,
        action.kind === 'resolve' ? { reason: reason.trim() } : {},
      );
      toast(
        action.kind === 'resend'
          ? `SA was asked to resend round ${action.issue.providerGameId ?? ''}. Its callbacks post as they arrive.`
          : `Finding for txn ${action.issue.txnId} marked resolved. The reason is in the audit trail.`,
      );
      setDialog(closedDialog<Action>());
      setReload((n) => n + 1);
    } catch (error) {
      /* Closed the same way as every other exit: a reason typed for THIS
         finding must never be waiting in the box for the next one. */
      setDialog(closedDialog<Action>());
      showError({
        title:
          action.kind === 'resend' ? 'The resend was not made' : 'The finding was not resolved',
        message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
      });
    } finally {
      setBusy(false);
    }
  }, [action, reason, showError, toast]);

  const closedBeforeOf = (i: ReconciliationIssue) =>
    closedBeforeLine(i, (iso) => WHEN.format(new Date(iso)), provider);

  /* SA only: what our ledger did with the bet is read from SA's PlaceBet records. */
  const ourSideColumn: Column<ReconciliationIssue> = {
    key: 'ourSide',
    header: 'Our side',
    render: (i) => {
      const closedBefore = closedBeforeOf(i);
      return (
        <span>
          {/* A sentence, so it wraps at a phone's edge rather than running off it. On the
              Resolved tab it is muted: a closed finding asks nothing of anybody. */}
          <Tag
            tone={status === 'RESOLVED' ? 'quiet' : (OUR_SIDE[i.ourSide]?.tone ?? 'quiet')}
            className="tag-wraps"
          >
            {ourSideLabel(i)}
          </Tag>
          {closedBefore !== null && (
            <span className="text-muted" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
              {closedBefore}
            </span>
          )}
        </span>
      );
    },
  };

  const columns: Column<ReconciliationIssue>[] = [
    {
      key: 'kind',
      header: 'Reason',
      render: (i) => {
        /* With no Our side column to carry it, a finding closed before and open again says so here. */
        const closedBefore = showOurSide ? null : closedBeforeOf(i);
        return (
          <span>
            <Tag tone="outline">{ISSUE_KIND[i.kind] ?? i.kind}</Tag>
            {closedBefore !== null && (
              <span className="text-muted" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                {closedBefore}
              </span>
            )}
          </span>
        );
      },
    },
    ...(showOurSide ? [ourSideColumn] : []),
    { key: 'txn', header: 'Txn ID', render: (i) => <code>{i.txnId}</code> },
    { key: 'round', header: `${tableName} game ID`, render: (i) => i.providerGameId ?? '—' },
    { key: 'player', header: 'Player', secondary: true, render: (i) => i.playerRef ?? '—' },
    {
      key: 'detail',
      header: 'Detail',
      secondary: true,
      render: (i) => (
        <span className="text-muted">
          {(provider === 'JILI'
            ? jiliDetailLines(i.detail, (iso) => WHEN.format(new Date(iso)))
            : saDetailLines(i.detail, (iso) => WHEN.format(new Date(iso)))
          ).map((line) => (
            <span key={line} style={{ display: 'block' }}>
              {line}
            </span>
          ))}
        </span>
      ),
    },
    ...(status === 'RESOLVED'
      ? [
          {
            key: 'resolution',
            header: 'Closed because',
            render: (i: ReconciliationIssue) => (
              <span>
                {i.resolutionReason ?? '—'}
                {(i.resolvedByName ?? i.resolvedBy) && (
                  <span className="text-muted" style={{ display: 'block' }}>
                    {i.resolvedByName ?? i.resolvedBy}
                    {i.resolvedAt === null ? '' : ` · ${WHEN.format(new Date(i.resolvedAt))}`}
                  </span>
                )}
              </span>
            ),
          },
        ]
      : []),
    {
      key: 'seen',
      header: 'First / last seen',
      secondary: true,
      render: (i) => (
        <span className="text-muted">
          {WHEN.format(new Date(i.firstSeenAt))}
          <br />
          {WHEN.format(new Date(i.lastSeenAt))}
        </span>
      ),
    },
    ...(canAct && status === 'OPEN'
      ? [
          {
            key: 'actions',
            header: 'Actions',
            render: (i: ReconciliationIssue) => (
              <span className="row" style={{ flexWrap: 'nowrap', gap: 'var(--space-2)' }}>
                {offersResend(provider) && i.providerGameId && (
                  <Button small onClick={() => setDialog(openDialog({ kind: 'resend', issue: i }))}>
                    Resend
                  </Button>
                )}
                <Button small onClick={() => setDialog(openDialog({ kind: 'resolve', issue: i }))}>
                  Resolve
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  const kinds = [ANY_KIND, ...kindsFor(provider)];
  /* An empty list needs an answer to be one: until there is one, the table says it is not loaded. */
  const empty =
    brandRestricted === null || check === null
      ? null
      : reconciliationEmpty(brandRestricted, status, filters.kind !== ANY_KIND, {
          providerName,
          check,
          formatWhen: (iso) => WHEN.format(new Date(iso)),
        });

  return (
    <>
      <div className="notice notice-quiet">
        <Icon name={canAct ? 'notebook' : 'lock-key'} />
        <div>
          {reconciliationLead(brandRestricted, provider)}{' '}
          {provider === 'SA' ? (
            <>
              {canAct
                ? 'Resend asks SA to replay a round’s callbacks — once every five minutes, SA’s rule. Resolve closes a finding you have dealt with.'
                : 'Resend and Resolve need the Submit balance adjustment permission, so they are not shown to you.'}{' '}
              Our side says what our own ledger did with the bet. On a bet SA lists as unsuccessful,
              only &ldquo;Stake taken, not settled&rdquo; can leave a player owed: close the others with a
              reason and never pay them by hand. On the other reasons the two records disagree, so
              compare them with SA&rsquo;s statement first.
            </>
          ) : (
            <>
              {canAct
                ? 'JILI has no resend, so the only action is Resolve, which closes a finding you have dealt with.'
                : 'Resolve needs the Submit balance adjustment permission, so it is not shown to you.'}{' '}
              Nothing here has been corrected for you: each finding is a difference to look into with
              JILI&rsquo;s own statement, and a player may be owed or may owe.
            </>
          )}{' '}
          A hundred findings load at a time; times are Manila time.
        </div>
      </div>

      <div className="row">
        <Field label="Provider">
          {({ id }) => (
            <Select
              id={id}
              value={provider}
              onChange={(e) => changeFilters({ provider: e.target.value, kind: ANY_KIND })}
            >
              {PROVIDER_OPTIONS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Chips
          label="Status"
          options={[
            { key: 'OPEN', label: 'Open' },
            { key: 'RESOLVED', label: 'Resolved' },
          ]}
          active={filters.status}
          onChange={(v) => changeFilters({ status: v })}
        />
        <Chips
          label="Reason"
          options={kinds.map((k) => ({
            key: k,
            label: k === ANY_KIND ? 'All reasons' : (ISSUE_KIND[k] ?? k),
          }))}
          active={filters.kind}
          onChange={(v) => changeFilters({ kind: v })}
        />
      </div>

      {/*
        * THE RANGE IS ONE CONTROL, SO IT IS ONE ROW WITH ONE HINT.
        *
        * It used to hang the hint under the FIRST field only, which made that
        * column taller than the second, so the two boxes and the buttons each
        * sat at a different height — the buttons carried an `alignSelf` to
        * paper over a misalignment the hint had caused. The hint applies to
        * BOTH dates, so it belongs under the pair, and with the columns the
        * same height everything shares one baseline without compensating.
        *
        * `alignItems: flex-end` rather than the `.row` default of `center`:
        * a label sits above its box, so centring aligns the MIDDLE of a
        * label-plus-box against the middle of a bare button, which is what put
        * the buttons visibly high.
        */}
      <form
        className="stack"
        style={{ gap: 'var(--space-2)' }}
        onSubmit={(e) => {
          e.preventDefault();
          changeFilters(draftDates);
        }}
      >
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <Field label="Last seen from">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                /* Bounded so an empty date box does not stretch to the width of
                   the panel; a date is never longer than this. */
                style={{ maxWidth: '11rem' }}
                value={draftDates.from}
                max={draftDates.to || undefined}
                onChange={(e) => setDraftDates({ ...draftDates, from: e.target.value })}
              />
            )}
          </Field>
          <Field label="Last seen to">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                style={{ maxWidth: '11rem' }}
                value={draftDates.to}
                /* The two ends constrain each other, so a range that reads
                   backwards cannot be submitted and then refused. */
                min={draftDates.from || undefined}
                onChange={(e) => setDraftDates({ ...draftDates, to: e.target.value })}
              />
            )}
          </Field>
          <Button type="submit" variant="primary">
            Search
          </Button>
          <Button
            type="button"
            /* Off when there is nothing to clear: a control that does nothing
               reads as one that is broken. */
            disabled={!draftDates.from && !draftDates.to && !filters.from && !filters.to}
            onClick={() => {
              setDraftDates({ from: '', to: '' });
              changeFilters({ from: '', to: '' });
            }}
          >
            Clear
          </Button>
        </div>
        <div className="field-hint">
          Manila days, inclusive. The box follows your browser&rsquo;s date format, which may be
          mm/dd/yyyy — the day it means is always Manila&rsquo;s.
        </div>
      </form>

      <Panel>
        {loading ? (
          <div className="skeleton" style={{ height: '200px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption={`${tableName} reconciliation findings with reason, txnid, ${tableName} game ID and when they were seen`}
            columns={columns}
            rows={issues}
            rowKey={(i) => i.id}
            empty={
              failed || empty === null ? (
                <EmptyState icon="warning" title="Not loaded">
                  The findings could not be read, so this is not a zero.
                </EmptyState>
              ) : (
                <EmptyState icon="notebook" title={empty.title}>
                  {empty.body}
                </EmptyState>
              )
            }
          />
        )}
        {nextCursor && !loading && (
          <div className="row" style={{ justifyContent: 'center' }}>
            <Button
              busy={loadingMore}
              onClick={() => {
                setLoadingMore(true);
                void load(nextCursor).finally(() => setLoadingMore(false));
              }}
            >
              Load more
            </Button>
          </div>
        )}
      </Panel>

      <Dialog
        open={action !== null}
        onClose={() => {
          setDialog(closedDialog<Action>());
        }}
        dismissible={!busy}
        title={
          action?.kind === 'resend' ? 'Ask SA to resend this round?' : 'Mark this finding resolved?'
        }
        actions={
          <>
            <Button
              onClick={() => {
                setDialog(closedDialog<Action>());
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={action?.kind === 'resolve' && reasonError !== null}
              onClick={() => void confirm()}
            >
              {action?.kind === 'resend' ? 'Resend' : 'Resolve'}
            </Button>
          </>
        }
      >
        {action &&
          (() => {
            const closedBefore = closedBeforeLine(action.issue, (iso) => WHEN.format(new Date(iso)), provider);
            if (provider === 'JILI')
              return (
                <div className="notice notice-warn">
                  <Icon name="warning" />
                  <div>
                    <strong>{ISSUE_KIND[action.issue.kind] ?? action.issue.kind}.</strong>{' '}
                    {JILI_KIND_MEANING[action.issue.kind] ?? ''} Compare it with JILI&rsquo;s statement
                    before you close it.
                    {closedBefore !== null && <span style={{ display: 'block' }}>{closedBefore}</span>}
                  </div>
                </div>
              );
            const verdict = verdictFor(action.issue);
            return (
              <div className={`notice ${verdict.tone === 'warn' ? 'notice-warn' : 'notice-quiet'}`}>
                <Icon name={verdict.tone === 'warn' ? 'warning' : 'check-circle'} />
                <div>
                  <strong>{ourSideLabel(action.issue)}.</strong> {verdict.text}
                  {closedBefore !== null && <span style={{ display: 'block' }}>{closedBefore}</span>}
                </div>
              </div>
            );
          })()}
        {action && (
          <p>
            {action.kind === 'resend'
              ? `SA replays every callback for game ID ${action.issue.providerGameId}. None moves money twice: one we already applied changes nothing, and one we refused is checked again. SA allows this once every five minutes.`
              : `Txn ${action.issue.txnId} (${ISSUE_KIND[action.issue.kind] ?? action.issue.kind}) leaves the open list. Nothing is posted or reversed by resolving it.`}
          </p>
        )}
        {action?.kind === 'resolve' && (
          <Field
            label="Why"
            hint="What was checked, and what the answer was. It goes in the audit trail beside your name."
            error={reasonTouched ? (reasonError ?? undefined) : undefined}
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                maxLength={RESOLVE_REASON_MAX}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={reason}
                onChange={(e) => setDialog((d) => ({ ...d, reason: e.target.value }))}
                onBlur={() => setDialog((d) => ({ ...d, reasonTouched: true }))}
                placeholder={
                  provider === 'JILI'
                    ? 'Compared with JILI’s statement: what each side shows, and what was done about it.'
                    : resolvePlaceholder(action.issue)
                }
              />
            )}
          </Field>
        )}
      </Dialog>
    </>
  );
}
